import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import parse from "html-react-parser";
import {
  Col,
  Collapse,
  Descriptions,
  Empty,
  Form,
  Input,
  Row,
  Skeleton,
} from "antd";
import { invert } from "lodash";
import formatDateTime from "helpers/formatDateTime";

import { useLazyGetEnumInfoQuery } from "store/system";
import { Detail } from "types/datacontrol.types";
import { useGetHistoryQuery } from "store/datacontrol";
import sortByPropertyName from "views/App/views/Masterdata/shared/sortByPropertyName";

interface Props {
  kundennummer: number;
  refetchHistoryTab: boolean;
  historyActionValues: Record<string, number>;
}

function HistoryTab({
  kundennummer,
  refetchHistoryTab,
  historyActionValues,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const [getEnumInfo] = useLazyGetEnumInfoQuery();
  const { data, isLoading, isFetching, refetch } = useGetHistoryQuery({
    kundennummer,
  });
  const [dkZuteilungValues, setDkZuteilungValues] = useState<
    Record<string, number>
  >({});
  const [dkStatusValues, setDkStatusValues] = useState<Record<string, number>>(
    {}
  );
  const [mahnungActionValues, setMahnungActionValues] = useState<
    Record<string, number>
  >({});
  const [historyActionEnum, setHistoryActionEnum] = useState<
    Record<string, number>
  >({});
  const [rekStatusValues, setRekStatusValues] = useState<
    Record<string, number>
  >({});
  const [rekSubStatusValues, setRekSubStatusValues] = useState<
    Record<string, number>
  >({});

  const getEnum = async (
    name: string,
    setter: React.Dispatch<React.SetStateAction<Record<string, number>>>
  ) => {
    try {
      const payload = await getEnumInfo({
        enumName: name,
      }).unwrap();
      if (payload) {
        setter(payload);
      }
    } catch (error) {
      console.error("GetEnumInfo Error", error);
    }
  };

  useEffect(() => {
    getEnum("DkZuteilungEnum", setDkZuteilungValues);
    getEnum("DKStatusEnum", setDkStatusValues);
    getEnum("MahnungActionEnum", setMahnungActionValues);
    getEnum("HistoryActionEnum", setHistoryActionEnum);
    getEnum("RekStatusEnum", setRekStatusValues);
    getEnum("RekSubStatusEnum", setRekSubStatusValues);
  }, []);

  useEffect(() => {
    refetch();
  }, [refetchHistoryTab]);

  const renderDetail = (detail: Detail) => {
    const { propertyName: initialPropertyName } = detail;
    let { newValue, oldValue, propertyName } = detail;
    if (propertyName === "TicketLink" && typeof newValue === "string") {
      newValue = (
        <a href={newValue} target="_blank" rel="noreferrer">
          {newValue}
        </a>
      );
    }
    if (propertyName === "TicketLink" && typeof oldValue === "string") {
      oldValue = (
        <a href={oldValue} target="_blank" rel="noreferrer">
          {oldValue}
        </a>
      );
    }
    if (propertyName === "Entschaedigung" && typeof newValue === "boolean") {
      propertyName = t("data_control.data_control_tab.form.entschaedigung");
    }
    if (propertyName === "DkStatus" && typeof newValue === "number") {
      propertyName = t("data_control.data_control_tab.history.dkStatus");
      newValue =
        (t(`masterdata.enums.DKStatusEnum.${newValue}`) as string) ||
        invert(dkStatusValues)[newValue];
    }
    if (propertyName === "DkZuteilung" && typeof newValue === "number") {
      propertyName = t("data_control.data_control_tab.history.dkZuteilung");
      newValue = invert(dkZuteilungValues)[newValue];
    }
    if (propertyName === "MahnungAction" && typeof newValue === "number") {
      propertyName = t("data_control.data_control_tab.history.mahnungAction");
      newValue =
        (t(`masterdata.enums.MahnungActionEnum.${newValue}`) as string) ||
        invert(mahnungActionValues)[newValue];
    }
    if (propertyName === "RekStatus" && typeof newValue === "number") {
      newValue =
        (t(`masterdata.enums.RekStatusEnum.${newValue}`) as string) ||
        invert(rekStatusValues)[newValue];
    }
    if (propertyName === "RekSubStatus" && typeof newValue === "number") {
      newValue =
        (t(`masterdata.enums.RekSubStatusEnum.${newValue}`) as string) ||
        invert(rekSubStatusValues)[newValue];
    }
    if (propertyName === "Language" && typeof newValue === "number") {
      newValue =
        (t(`masterdata.enums.LanguageEnum.${newValue}`) as string) || newValue;
    }
    if (propertyName === "Note") {
      propertyName = t("data_control.data_control_tab.history.note");
    }
    if (oldValue || typeof oldValue === "boolean") {
      if (initialPropertyName === "DkStatus" && typeof oldValue === "number") {
        propertyName = t("data_control.data_control_tab.history.dkStatus");
        oldValue =
          (t(`masterdata.enums.DKStatusEnum.${oldValue}`) as string) ||
          invert(dkStatusValues)[oldValue];
      }
      if (
        initialPropertyName === "DkZuteilung" &&
        typeof oldValue === "number"
      ) {
        propertyName = t("data_control.data_control_tab.history.dkZuteilung");
        oldValue = invert(dkZuteilungValues)[oldValue];
      }
      return (
        <React.Fragment key={`${oldValue} ${propertyName}`}>
          <Descriptions.Item label={`${propertyName} alt`}>
            {propertyName === "TicketLink" ? oldValue : parse(`${oldValue}`)}
          </Descriptions.Item>
          <Descriptions.Item label={`${propertyName} neu`}>
            {propertyName === "TicketLink" ? newValue : parse(`${newValue}`)}
          </Descriptions.Item>
        </React.Fragment>
      );
    }

    if (propertyName === "EmailAddress") {
      propertyName = t("data_control.data_control_tab.history.emailAddress");
    }
    if (propertyName === "EmailSubject") {
      propertyName = t("data_control.data_control_tab.history.emailSubject");
    }
    if (propertyName === "EmailText") {
      propertyName = t("data_control.data_control_tab.history.emailText");
    }
    if (propertyName === "TicketLink") {
      propertyName = t("data_control.data_control_tab.history.ticketLink");
    }

    return (
      <Descriptions.Item
        label={`${propertyName}`}
        key={`${oldValue} ${propertyName}`}
        contentStyle={{ display: "block" }}
      >
        {["TicketLink", "Link"].includes(propertyName)
          ? newValue
          : parse(`${newValue}`)}
      </Descriptions.Item>
    );
  };

  const renderHistory = useMemo(() => {
    if (historyActionValues) {
      return data?.historyEntryList?.map(
        ({ createdAtUtc, details, category, user }) => {
          const note = details.find((detail) => detail.propertyName === "Note");
          const header = `${formatDateTime({ date: createdAtUtc })} - ${t(
            `masterdata.enums.HistoryActionEnum.${category}`,
            (historyActionEnum
              ? invert(historyActionEnum)[category]
              : category) as string
          )} - ${user || "--"}`;

          const sortedDetails = sortByPropertyName(category, details);
          const preparedDetails =
            sortedDetails.length > 0 ? sortedDetails : details;
          return (
            <Collapse.Panel
              header={header}
              key={`${createdAtUtc} - ${category}`}
            >
              <Descriptions column={1}>
                <Descriptions.Item label="User">
                  {user || "--"}
                </Descriptions.Item>
                {preparedDetails.map((detail) => {
                  if (detail.propertyName === "Note") {
                    return null;
                  }
                  const tempDetail = { ...detail };
                  if (
                    typeof detail.oldValue === "string" &&
                    !detail.oldValue.startsWith("<")
                  ) {
                    tempDetail.oldValue = detail?.oldValue
                      ?.replace(/</g, "&lt;")
                      ?.replace(/>/g, "&gt;");
                  }
                  if (
                    typeof detail.newValue === "string" &&
                    !detail.newValue.startsWith("<")
                  ) {
                    tempDetail.newValue = detail?.newValue
                      ?.replace(/</g, "&lt;")
                      ?.replace(/>/g, "&gt;");
                  }
                  return renderDetail(tempDetail);
                })}
                {note?.newValue && renderDetail(note)}
              </Descriptions>
            </Collapse.Panel>
          );
        }
      );
    }
    return null;
  }, [data, historyActionValues, historyActionEnum]);

  return (
    <Row>
      <Col span={24}>
        <Row gutter={20}>
          {data?.lastLoginDl && (
            <Col span={8}>
              <Form.Item label="Letze Aktivität am">
                <Input
                  value={formatDateTime({ date: data.lastLoginDl })}
                  readOnly
                />
              </Form.Item>
            </Col>
          )}
        </Row>
      </Col>
      <Col span={24}>
        <Skeleton active loading={isLoading || isFetching}>
          {data && data?.historyEntryList.length > 0 ? (
            <Collapse>{renderHistory}</Collapse>
          ) : (
            <Empty
              description={t("data_control.data_control_tab.history.no_data")}
            />
          )}
        </Skeleton>
      </Col>
    </Row>
  );
}

export default HistoryTab;
